import { Component, computed } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SidebarService } from '../../services/sidebar.service';
import { BreadcrumbComponent } from '../../app/shared/components/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-full-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    MatSidenavModule,
    HeaderComponent,
    //FooterComponent,
    SidebarComponent,
    BreadcrumbComponent,
  ],
  template: `
    <mat-sidenav-container class="app-container">
      <!-- Sidebar -->
      <mat-sidenav
        #sidenav
        [mode]="isMobileOrTablet() ? 'over' : 'side'"
        [opened]="sidenavOpened()"
        (openedChange)="onSidenavOpenedChange($event)"
        fixedInViewport="false"
        class="app-sidenav"
        [class.collapsed]="isCollapsed()"
        [style.width.px]="!isMobileOrTablet() ? sidenavWidth() : null"
      >
        <app-sidebar></app-sidebar>
      </mat-sidenav>

      <!-- Main content -->
      <mat-sidenav-content class="app-main-content">
        <!-- Header -->
        <app-header class=""></app-header>
        <!-- breadcrumb -->
        <app-breadcrumb style="margin-top: 65px;"></app-breadcrumb>

        <!-- Page content -->
        <main class="page-content">
          <div>
            <router-outlet></router-outlet>
          </div>
        </main>

        <!-- Footer -->
        <!-- <app-footer></app-footer> -->
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: [
    `
      /* Reset básico para eliminar espacios por defecto */
      * {
        box-sizing: border-box;
      }

      .app-container {
        height: 100vh;
        width: 100vw;
        display: flex;
        margin: 0;
        padding: 0;
        background-color: var(--color-background);
      }

      .app-sidenav {
        min-width: 72px;
        max-width: 280px;
        // border-right: 1px solid var(--bs-border-color);
        box-shadow: var(--shadow-sm);
        transition: width 0.3s ease;
        background-color: var(--color-surface);
      }

      /* En modo 'over' (móvil/tablet), fijar el ancho a 280px */
      .app-sidenav[mode='over'] {
        width: 280px !important;
        min-width: 280px;
        max-width: 280px;
      }

      .app-sidenav.collapsed {
        /* Ancho controlado dinámicamente por [style.width.px] en desktop */
      }

      .app-main-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        height: 100vh;
        overflow: hidden;
        margin: 0 !important;
        padding: 0;
        background-color: var(--color-background);
      }

      .page-content {
        flex: 1;
        overflow-y: auto;
        padding-top: 20px; /* Header height */
        padding-bottom: 48px; /* Footer height */
        margin: 0; /* Sin márgenes */
        min-height: calc(100vh - 112px); /* Total minus header and footer */
        width: 100%; /* Ancho completo */
        box-sizing: border-box;
        background-color: var(--color-background);
      }

      /* El container dentro del page-content debe mantener su comportamiento */
      .page-content .container {
        /* Permitir que Bootstrap container funcione normalmente */
        margin-left: auto;
        margin-right: auto;
        padding-left: var(--bs-gutter-x, 0.75rem);
        padding-right: var(--bs-gutter-x, 0.75rem);
        width: 100%;
      }

      /* Asegurar que router-outlet no interfiera */
      router-outlet {
        display: block;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .app-sidenav {
          max-width: 280px;
          min-width: 0;
        }

        .page-content {
          padding-top: 56px; /* Smaller header height on mobile */
          padding-bottom: 48px;
          min-height: calc(100vh - 104px);
        }
      }

      @media (min-width: 769px) and (max-width: 1024px) {
        .app-sidenav {
          max-width: 280px;
          min-width: 0;
        }

        .page-content {
          padding-top: 64px; /* Normal header height on tablet */
          padding-bottom: 48px;
          min-height: calc(100vh - 112px);
        }
      }

      @media (max-width: 480px) {
        .app-sidenav {
          position: fixed;
          height: 100%;
          z-index: 1000;
          max-width: 280px;
          min-width: 0;
        }
      }

      @media (min-width: 1025px) {
        .app-sidenav {
          position: relative; /* Solo en desktop necesitamos position relative */
        }
      }

      /* Override Material Design default styles */
      .mat-sidenav-container {
        background-color: var(--color-background);
        margin: 0;
        padding: 0;
      }

      .mat-sidenav-content {
        background-color: var(--color-background);
        margin: 0;
        padding: 0;
      }
    `,
  ],
})
export class FullLayoutComponent {
  constructor(private sidebarService: SidebarService) {}

  sidenavWidth = computed(() => this.sidebarService.sidenavWidth());
  isCollapsed = computed(() => this.sidebarService.isCollapsed());
  isMobile = computed(() => this.sidebarService.isMobile());
  isTablet = computed(() => this.sidebarService.isTablet());
  isMobileOrTablet = computed(() => this.sidebarService.isMobileOrTablet());

  // Computed específico para el estado abierto del sidenav
  sidenavOpened = computed(() => {
    if (this.isMobileOrTablet()) {
      // En móvil/tablet, el sidenav está abierto solo cuando NO está colapsado
      return !this.isCollapsed();
    } else {
      // En desktop, el sidenav siempre está abierto (modo side)
      return true;
    }
  });

  onSidenavOpenedChange(opened: boolean) {
    // Solo actualizar el estado en móvil/tablet cuando el sidenav se cierra automáticamente
    if (this.isMobileOrTablet() && !opened) {
      this.sidebarService.setCollapsed(true);
    }
  }
}
